import { FC, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { formatNumber } from 'utils/formats';

import { CoOwner } from 'types';

import { RowCell } from 'modules/_shared/Table/types/RowCell';
import TableRow from 'modules/_shared/Table/components/TableRow';

import ToggleMenu from 'modules/_shared/components/ToggleMenu';
import Button, { ButtonVariants } from 'modules/_shared/components/Buttons';
import { useEncumbranceDetailsPage } from '../context/EncumbranceDetailsPageContext';

type CoOwnersTableRowProps = {
  index: number;
  coOwner: CoOwner;
};

const CoOwnersTableRow: FC<CoOwnersTableRowProps> = ({ index, coOwner }) => {
  const { t } = useTranslate();
  const { data, actions } = useEncumbranceDetailsPage();
  const [isExpanded, setIsExpanded] = useState(true);

  const handleEdit = () => {
    actions.handleEditCoOwner(index);
  };

  const handleRemove = () => {
    actions.handleRemoveCoOwner(index);
  };

  const toggleOptions = [
    <a
      className="d-flex cursor-pointer"
      key={`co-owner-edit-option-${index}`}
      onClick={handleEdit}
    >
      <em className="icon ni ni-edit fs-20px" />
      <span className="ml-1">{t('Edit')}</span>
    </a>,
    <a
      className="d-flex cursor-pointer"
      key={`encumbrance-remove-option-${index}`}
      onClick={handleRemove}
    >
      <em className="icon ni ni-trash fs-20px" />
      <span className="ml-1">{t('Delete')}</span>
    </a>,
  ];

  const hasOptions = data.isAdmin && !data.isReadOnly;
  const columnCount = hasOptions ? 6 : 5;

  const cells = [
    {
      key: `co-owner-expand-${index}`,
      className: 'coOwners-table__expand-col',
      content: (
        <Button
          className="badge"
          onClick={() => setIsExpanded((prev) => !prev)}
          variant={ButtonVariants.LIGHT}
        >
          <em
            className={`icon ni ni-chevron-${isExpanded ? 'up' : 'down'}`}
          />
        </Button>
      ),
    },
    {
      key: `co-owner-name-${index}`,
      content: coOwner?.name || '-',
    },

    {
      key: `co-owner-cif-${index}`,
      content: coOwner?.cif || '-',
    },
    {
      key: `co-owner-email-${index}`,
      content: coOwner?.email || '-',
    },

    {
      key: `co-owner-percent-${index}`,
      content: coOwner?.percentage
        ? `${formatNumber(coOwner.percentage)}%`
        : '-',
      textPosition: 'right',
    },
    hasOptions
      ? {
          key: `co-owner-options-${index}`,
          content: <ToggleMenu toggleOptions={toggleOptions} />,
          textPosition: 'center',
        }
      : null,
  ].filter(Boolean) as RowCell[];

  return (
    <>
      <TableRow cells={cells} />

      {isExpanded && (
        <tr className="nk-tb-item bg-light inner-shadow">
          <td colSpan={columnCount} className="nk-tb-col">
            <span className="text-soft">{t('Comment')}: </span>
            {coOwner?.comment || '-'}
          </td>
        </tr>
      )}
    </>
  );
};

export default CoOwnersTableRow;
